
import React from 'react';
import { Control } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl, FormDescription } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { InfoTooltip } from '../ui/InfoTooltip';
import { FormCard } from '../ui/FormCard';
import { MoneyInput } from '../ui/MoneyInput';

interface AssetsSectionProps {
  control: Control<any>;
  applicantStatus: string;
  ownsPrimaryResidence: boolean;
  hasOtherProperties: boolean;
  hasFarmOrBusiness: boolean;
}

export function AssetsSection({ 
  control, 
  applicantStatus,
  ownsPrimaryResidence,
  hasOtherProperties,
  hasFarmOrBusiness
}: AssetsSectionProps) {
  const exemptionAmount = applicantStatus === 'single' ? '€36,000' : '€72,000';

  return (
    <>
      <FormCard 
        title="Cash Assets" 
        description={`The first ${exemptionAmount} of your assets is exempt (${applicantStatus === 'single' ? 'single person' : 'couple'})`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={control}
            name="savingsAndCash"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  Savings & Cash (€)
                  <InfoTooltip content="Include bank accounts, credit union accounts, cash deposits, etc." />
                </FormLabel>
                <FormControl>
                  <MoneyInput {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="investments"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  Investments (€)
                  <InfoTooltip content="Stocks, shares, bonds, investment funds, etc." />
                </FormLabel>
                <FormControl>
                  <MoneyInput {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="otherAssets"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel className="flex items-center gap-2">
                  Other Cash Assets (€)
                  <InfoTooltip content="Any other liquid assets not included above, such as money owed to you." />
                </FormLabel>
                <FormControl>
                  <MoneyInput {...field} />
                </FormControl>
                <FormDescription>
                  Include any other cash or financial assets not covered above, including money owed to you.
                </FormDescription>
              </FormItem>
            )}
          />
        </div>
      </FormCard>

      <FormCard title="Property Assets" description="Information about your property holdings">
        <div className="space-y-6">
          <FormField
            control={control}
            name="ownsPrimaryResidence"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    I own my primary residence (family home)
                    <InfoTooltip content="Your main residence is subject to the 3-year cap under Fair Deal." />
                  </FormLabel>
                  <FormDescription>
                    The home you lived in before requiring nursing home care.
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />

          {ownsPrimaryResidence && (
            <FormField
              control={control}
              name="primaryResidenceValue"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    Primary Residence Value (€)
                    <InfoTooltip content="The estimated market value of your home. This is subject to the 3-year cap under Fair Deal." />
                  </FormLabel>
                  <FormControl>
                    <MoneyInput {...field} />
                  </FormControl>
                  <FormDescription>
                    Your home will only be assessed for the first 3 years of care (7.5% per year, up to 22.5% total).
                  </FormDescription>
                </FormItem>
              )}
            />
          )}

          <FormField
            control={control}
            name="hasOtherProperties"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    I own other properties
                    <InfoTooltip content="Additional properties are assessed at 7.5% of their value each year, with no cap." />
                  </FormLabel>
                  <FormDescription>
                    Any properties other than your primary residence.
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />

          {hasOtherProperties && (
            <FormField
              control={control}
              name="otherPropertiesValue"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    Other Properties Total Value (€)
                    <InfoTooltip content="The combined market value of all your additional properties. These are assessed indefinitely." />
                  </FormLabel>
                  <FormControl>
                    <MoneyInput {...field} />
                  </FormControl>
                  <FormDescription>
                    These properties will be assessed at 7.5% each year with no 3-year cap.
                  </FormDescription>
                </FormItem>
              )}
            />
          )}
        </div>
      </FormCard>

      <FormCard title="Farm or Business Assets" description="Information about any farm or business you own">
        <div className="space-y-6">
          <FormField
            control={control}
            name="hasFarmOrBusiness"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    I own a farm or business
                    <InfoTooltip content="Farms and businesses are normally assessed at 7.5% per year, but may qualify for the 3-year cap if certain conditions are met." />
                  </FormLabel>
                  <FormDescription>
                    Any farm, business premises, or business assets you own.
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />

          {hasFarmOrBusiness && (
            <>
              <FormField
                control={control}
                name="farmBusinessValue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      Farm/Business Value (€)
                      <InfoTooltip content="The estimated market value of your farm or business assets." />
                    </FormLabel>
                    <FormControl>
                      <MoneyInput {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="familySuccessor"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        Family successor for farm/business
                        <InfoTooltip content="To qualify for the 3-year cap, a family member must take over the farm/business for at least 6 years, and it must have been actively operated by you, your partner, or the successor for 3 out of the last 5 years." />
                      </FormLabel>
                      <FormDescription>
                        If a family member will continue to run the farm/business, it may qualify for the 3-year cap.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </>
          )}
        </div>
      </FormCard>
    </>
  );
}
